import type { RuleArea } from "@unbox/api";
import { CheckSquare, Plus, Repeat03, ZapFast } from "@untitledui/icons";
import { useState } from "react";
import { useRules, useRulesImpact } from "@/api/rules";
import { useDismissHero } from "@/api/user-preferences";
import { PageHero, PageHeroKey } from "@/components/application/page-hero/page-hero";
import { Button } from "@/components/base/buttons/button";
import { PageLayout } from "@/components/page-layout";
import { pluralize } from "@/utils/text";
import { DeleteRuleDialog, type DeleteRuleTarget } from "./delete-rule-dialog";
import { NewRuleDialog } from "./new-rule-dialog";
import { RuleBuilder } from "./rule-builder";
import { RulesEmptyState } from "./rules-empty-state";
import { RulesTable } from "./rules-table";
import { StarterRules } from "./starter-rules";

export function RulesPage() {
  const { data: rules, isPending } = useRules();
  const dismissHero = useDismissHero();
  const { data: impact, isPending: isCountingImpact, isError: impactFailed } = useRulesImpact();
  const [pickingArea, setPickingArea] = useState(false);
  const [building, setBuilding] = useState<{ area: RuleArea; ruleId?: string }>();
  const [deleting, setDeleting] = useState<DeleteRuleTarget>();
  const [browsingSuggested, setBrowsingSuggested] = useState(false);

  const items = rules?.items ?? [];
  const impacted = new Map((impact?.items ?? []).map((item) => [item.ruleId, item.impactedCount]));
  const enabledCount = items.filter((rule) => rule.isActive).length;

  const build = (area: RuleArea, ruleId?: string) => {
    setPickingArea(false);
    setBuilding({ area, ruleId });
  };

  const dialogs = (
    <>
      <NewRuleDialog open={pickingArea} onOpenChange={setPickingArea} onPick={build} />
      <DeleteRuleDialog target={deleting} onClose={() => setDeleting(undefined)} />
      {building && (
        <RuleBuilder
          area={building.area}
          ruleId={building.ruleId}
          onCreated={() => {
            if (items.length === 0) dismissHero.mutate(PageHeroKey.Rules);
          }}
          open
          onOpenChange={(open) => {
            if (!open) setBuilding(undefined);
          }}
        />
      )}
    </>
  );

  if (!isPending && items.length === 0 && !browsingSuggested) {
    return (
      <>
        <RulesEmptyState onCreate={() => setPickingArea(true)} onBrowseSuggested={() => setBrowsingSuggested(true)} />
        {dialogs}
      </>
    );
  }

  return (
    <PageLayout
      title="Rules"
      icon={ZapFast}
      subtitle="Automate actions across your creator network"
      callToAction={
        <Button color="primary" iconLeading={Plus} onClick={() => setPickingArea(true)}>
          New Rule
        </Button>
      }
    >
      <PageHero
        pageKey={PageHeroKey.Rules}
        title="Act on creator activity automatically"
        text1={{ icon: ZapFast, message: "Pick the creator action a rule watches for." }}
        text2={{
          icon: CheckSquare,
          message: "Choose what happens next: group by cohort, block or auto-action sample request.",
        }}
        text3={{ icon: Repeat03, message: "A rule keeps running until you turn it off." }}
        image="/heroes/rules.png"
      />
      <StarterRules onStart={build} />

      <section className="flex flex-col gap-4">
        <div className="flex items-baseline gap-2">
          <h2 className="text-base font-medium text-primary">Your rules</h2>
          <span className="text-sm text-tertiary">({pluralize(enabledCount, "rule", "rules")} enabled)</span>
        </div>
        <div className="overflow-hidden rounded-xl bg-primary">
          <RulesTable
            rules={items}
            impacted={impacted}
            isCountingImpact={isCountingImpact}
            impactFailed={impactFailed}
            onEdit={(rule) => build(rule.area, rule.ruleId)}
            onDelete={(rule) =>
              setDeleting({
                ruleId: rule.ruleId,
                area: rule.area,
                name: rule.name,
                impactedCount: impacted.get(rule.ruleId),
              })
            }
          />
        </div>
      </section>

      {dialogs}
    </PageLayout>
  );
}
